<template>
  <section class="principal" aria-label="Manager message">
    <div class="container">
      <div ref="card" class="card" :class="{ 'is-visible': isVisible }">
        <div class="media">
          <div class="image-wrap">
            <img class="principal-image" :src="managerImage" :alt="`Photo of ${managerName}`" loading="lazy" decoding="async" />
            <div class="image-overlay" aria-hidden="true"></div>
          </div>
        </div>
        <div class="content">
          <span class="eyebrow">Principal's Message</span>
          <h2 class="title">{{ managerName }}</h2>
          <blockquote class="quote" aria-label="Manager quote">
            <div class="quote-text" v-html="managerMessageEn"></div>
            <div v-if="managerMessageAr" class="quote-text quote-text-ar" dir="rtl" v-html="managerMessageAr"></div>
          </blockquote>
          <div class="meta">
            <span class="role">{{ managerRole }}</span>
            <span class="dot" aria-hidden="true"></span>
            <span class="brand">Al Shola American School</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';
import { mediaUrl, resolveRichText } from '@/services/media';

export default {
  name: 'Principal',
  data() {
    return {
      isVisible: false,
      observer: null,
      managerName: 'Nada Ibrahim Barakeh',
      managerRole: 'SAS Principal',
      managerMessageEn: 'As I reflect on the journey of this school year at Al Shola American School, I\'m overcome with a sense of pride and emotion that words can barely capture.',
      managerMessageAr: '',
      managerImage: new URL('../assets/Principal.png', import.meta.url).href
    }
  },
  async created() {
    try {
      // Call individual endpoint: /api/principal/
      const data = await api.getPrincipal();
      const list = api.extractList(data);
      // Principal endpoint returns array; take first active one
      const p = list.length > 0 ? list[0] : null;
      if (p) {
        if (p.name) this.managerName = p.name;
        // Serializer: title = alias for role
        if (p.title || p.role) this.managerRole = p.title || p.role;
        // Render HTML from CKEditor rich text
        if (p.message) this.managerMessageEn = resolveRichText(p.message);
        if (p.message_ar) this.managerMessageAr = resolveRichText(p.message_ar);
        if (p.photo) this.managerImage = mediaUrl(p.photo);
      }
    } catch (e) {
      console.warn('[Principal] API failed, using fallback:', e.message);
    }
  },
  mounted() {
    const el = this.$refs.card;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { this.isVisible = true; return; }
    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) { this.isVisible = true; this.observer?.disconnect(); this.observer = null; }
    }, { threshold: 0.25 });
    this.observer.observe(el);
  },
  beforeUnmount() { this.observer?.disconnect(); this.observer = null; }
}
</script>

<style scoped>
.principal { position: relative; background: white; padding: 40px 10px; overflow: hidden; }
.container { max-width: 1000px; margin: 0 auto; }
.card { position: relative; display: grid; grid-template-columns: minmax(350px, 40%) 1fr; min-height: 520px; border-radius: 24px; overflow: hidden; background: #fff; box-shadow: 0 25px 60px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04); transform: translateY(28px); opacity: 0; transition: transform 800ms cubic-bezier(0.22,1,0.36,1), opacity 800ms ease, box-shadow 400ms ease; }
.card.is-visible { transform: translateY(0); opacity: 1; }
.card:hover { box-shadow: 0 32px 72px rgba(0,0,0,0.1), 0 12px 32px rgba(192,33,48,0.06); }
.media { position: relative; min-height: 320px; }
.image-wrap { position: absolute; inset: 0; width: 100%; height: 100%; }
.principal-image { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; transition: transform 700ms cubic-bezier(0.22,1,0.36,1); }
.image-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.15) 100%); pointer-events: none; }
.content { display: flex; flex-direction: column; justify-content: center; padding: clamp(28px,4vw,48px) clamp(28px,4vw,44px); color: #1a1a1a; }
.eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 800; letter-spacing: 2.5px; text-transform: uppercase; color: #c02130; margin-bottom: 12px; }
.eyebrow::before { content: ""; width: 28px; height: 2px; border-radius: 2px; background: linear-gradient(90deg, #c02130, #e8a317); }
.title { font-size: clamp(28px,3.2vw,38px); line-height: 1.15; margin: 0 0 20px; font-weight: 800; letter-spacing: -0.5px; color: #1a1a1a; }
.quote { margin: 0 0 20px; padding: 0; padding-left: 20px; border-left: 3px solid rgba(192,33,48,0.35); position: relative; }
.quote-text { margin: 0; font-size: clamp(15px,1.6vw,16px); line-height: 1.8; color: #3d3d3d; font-weight: 400; }
.quote-text + .quote-text { margin-top: 14px; }
.quote-text-ar { font-size: clamp(14px,1.5vw,15px); line-height: 1.85; color: #444; }
.meta { margin-top: 8px; display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 700; color: #5a5a5a; }
.role { color: #2a2a2a; }
.dot { width: 4px; height: 4px; border-radius: 50%; background: #c02130; opacity: 0.7; }
.brand { background: linear-gradient(90deg, #c02130, #b81d2b); -webkit-background-clip: text; background-clip: text; color: transparent; font-weight: 800; }
@media (max-width: 900px) { .card { grid-template-columns: 1fr; grid-template-rows: minmax(320px, 42vh) auto; min-height: auto; } .media { position: relative; min-height: 320px; } .image-wrap { position: absolute; inset: 0; } .content { padding: 28px 24px 36px; text-align: left; } .eyebrow { justify-content: flex-start; } .meta { justify-content: flex-start; flex-wrap: wrap; } }
@media (max-width: 480px) { .card { grid-template-rows: minmax(280px, 50vh) auto; } .media { min-height: 280px; } }
@media (prefers-reduced-motion: reduce) { .card, .principal-image { transition: none !important; } .card:hover .principal-image { transform: none; } }
</style>
